import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralDatePicker, GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { SUBSCRIBE_PACKAGE_LIST } from 'src/constants/queryKeys';
import { networkCreateSubscribePackage, networkUpdateSubscribePackage } from 'src/network/subscribe-package/subscribe-package';
import { useFetchSubscribePackagePositionList } from 'src/network/subscribe-package/subscribe-package-position';
import { useFetchSubscribePackageTypeList } from 'src/network/subscribe-package/subscribe-package-type';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { calculateDiscountedPrice, generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { SkeletonForm } from 'src/components/skeleton';

const SubscribePackageForm = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { appInfo } = useAppInfoStore();
  const { modal, closeModal } = useFormsModalStore();

  const subscribePackageDetails = modal?.data;
  const isEditMode = !!subscribePackageDetails;

  const [startDate, setStartDate] = useState(new Date(subscribePackageDetails?.SubscribePackageDateStart));
  const [endDate, setEndDate] = useState(new Date(subscribePackageDetails?.SubscribePackageDateEnd));

  const landingDetails = modal?.data;
  const defaultValues = getDefaultValues(landingDetails);

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const [packageTypes, setPackageTypes] = useState([]);
  const [selectedPackageItem, setSelectedPackageItem] = useState('');

  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');

  const { data: packageTypesListData, isLoading: packageTypesListIsLoading } = useFetchSubscribePackageTypeList();
  const { data: packagePositions, isLoading: packagePositionsIsloanding } = useFetchSubscribePackagePositionList();

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    watch,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      SubscribePackageApplicationID: +projectId,
      SubscribePackageDateStart: new Date(startDate).toISOString(),
      SubscribePackageDateEnd: new Date(endDate).toISOString(),
      SubscribePackageAmountDefault: +values?.SubscribePackageAmountDefault,
      SubscribePackageAmountDiscount: +values?.SubscribePackageAmountDiscount,
      SubscribePackageDaysCount: +values?.SubscribePackageDaysCount,
      SubscribePackageOrderBy: +values?.SubscribePackageOrderBy,
      SubscribePackageIsEnable: values?.SubscribePackageIsEnable ? 1 : 0,
      SubscribePackageIsSpecial: values?.SubscribePackageIsSpecial ? 1 : 0,
      SubscribePackageTransactionAmountTypeID: selectedAmountType,
      SubscribePackageSubscribePackageTypeID: selectedPackageItem,
      SubscribePackageSubscribePackagePositionID: selectedPosition,
      SubscribePackageUserLevel: +values?.SubscribePackageUserLevel,
      SubscribePackageClubPoint: +values?.SubscribePackageClubPoint,
      SubscribePackageAmountFinal: calculateDiscountedPrice(
        +values?.SubscribePackageAmountDefault,
        +values?.SubscribePackageAmountDiscount
      ),
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateSubscribePackage(landingDetails?.SubscribePackageID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [SUBSCRIBE_PACKAGE_LIST] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateSubscribePackage(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [SUBSCRIBE_PACKAGE_LIST] });
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

    setAmountTypes(amountTypeList);
    setSelectedAmountType(subscribePackageDetails?.TransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  const amountOrginal = watch('SubscribePackageAmountDefault');
  const discountPercent = watch('SubscribePackageAmountDiscount');

  useEffect(() => {
    if (amountOrginal && discountPercent !== undefined) {
      const finalAmount = calculateDiscountedPrice(parseFloat(amountOrginal), parseFloat(discountPercent));
      setValue('SubscribePackageAmountFinal', finalAmount?.toLocaleString());
    }
  }, [amountOrginal, discountPercent, setValue]);

  useEffect(() => {
    if (packageTypesListData) {
      const packageTypes = generateSelectMenuItems(
        packageTypesListData?.data?.items,
        'SubscribePackageTypeLable',
        'SubscribePackageTypeID'
      );
      setPackageTypes(packageTypes);
      setSelectedPackageItem(subscribePackageDetails?.SubscribePackageSubscribePackageTypeID || packageTypes?.[0]?.id);
    }
  }, [packageTypesListData]);

  useEffect(() => {
    if (packagePositions) {
      const packageTypes = generateSelectMenuItems(
        packagePositions?.data.items,
        'SubscribePackagePositionLable',
        'SubscribePackagePositionID'
      );
      setPositions(packageTypes);
      setSelectedPosition(subscribePackageDetails?.SubscribePackageSubscribePackagePositionID || packageTypes?.[0]?.id);
    }
  }, [packagePositions]);

  const isShowLoading = packageTypesListIsLoading || packagePositionsIsloanding;

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              {isShowLoading ? (
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                    width: '100%',
                  }}
                >
                  <SkeletonForm number={10} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name="SubscribePackageTitle" type="text" label="عنوان" />
                    <RHFTextField name="SubscribePackageTitleSubtext" type="text" label="زیر عنوان" />

                    <RHFTextField name="SubscribePackageDaysCount" type="text" label="تعداد روزهای اشتراک" />
                    <RHFTextField name="SubscribePackageOrderBy" type="text" label="ترتیب نمایش" />

                    <RHFTextField name="SubscribePackageAmountDefault" type="text" label="ارزش اصلی بسته اشتراک" />

                    <RHFTextField name="SubscribePackageAmountDiscount" type="text" label="درصد تخفیف" />

                    <RHFTextField
                      name="SubscribePackageAmountFinal"
                      type="text"
                      label="مبلغ نهایی قابل پرداخت توسط کاربر"
                      readOnly={true}
                    />

                    <GeneralSelectMenu
                      label="نوع واحد پرداخت"
                      name="SubscribePackageTransactionAmountTypeID"
                      options={amountTypes}
                      value={selectedAmountType}
                      onChangeValue={setSelectedAmountType}
                    />

                    <GeneralDatePicker
                      minDate={new Date()}
                      label="تاریخ شروع"
                      name="DuelCreateTime"
                      value={startDate}
                      onChangeValue={setStartDate}
                    />

                    <GeneralDatePicker
                      minDate={startDate}
                      label="تاریخ پایان"
                      name="DuelExpireTime"
                      value={endDate}
                      onChangeValue={setEndDate}
                    />

                    <GeneralSelectMenu
                      label="نوع پکیج"
                      name="SubscribePackageSubscribePackageTypeID"
                      options={packageTypes}
                      value={selectedPackageItem}
                      onChangeValue={setSelectedPackageItem}
                    />

                    <GeneralSelectMenu
                      label="جایگاه"
                      name="SubscribePackageSubscribePackagePositionID"
                      options={positions}
                      value={selectedPosition}
                      onChangeValue={setSelectedPosition}
                    />

                    <RHFTextField name="SubscribePackageUserLevel" type="text" label="سطح کاربر" />

                    <RHFTextField name="SubscribePackageClubPoint" type="text" label="امتیاز باشگاه" />

                    <GeneralUpload
                      label="تصویر"
                      id="subscribe_package_image"
                      name="SubscribePackageImage"
                      afterChangeAction={(res) => {
                        setValue('SubscribePackageImage', res);
                        trigger('SubscribePackageImage');
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name="SubscribePackageDescription" multiline rows={6} label="توضیحات" />
                    <RHFTextField name="SubscribePackageTermsAndCondition" multiline rows={6} label="قوانین و مقررات" />
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 3,
                      gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
                    }}
                  >
                    <RHFSwitch name="SubscribePackageIsEnable" labelPlacement="end" label="آیا نمایش داده شود؟" />
                    <RHFSwitch name="SubscribePackageIsSpecial" labelPlacement="end" label="آیا بسته ویژه است؟" />
                  </Box>

                  <div className="flex items-center gap-4 mt-6">
                    <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                      ذخیره
                    </LoadingButton>
                    <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                      {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                    </LoadingButton>
                    <Button onClick={closeModal} variant="contained" color="error">
                      انصراف
                    </Button>
                  </div>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

export default SubscribePackageForm;
